<template>
  <v-container
    fill-height
    fluid
  >
    <v-layout wrap>
      <v-flex xs12 sm12 md12 lg12 class="my-0">
        <v-img
          :src="require('@/assets/TastingIQlogotype-withHSP.svg')"
          contain
          max-width="300"
          class="mb-1 my-0 mx-auto"
        />
      </v-flex>

      <v-flex md12 class="my-0">
        <material-card
          color="secondary"
          title="Shared Schedule View"
        >
          <v-layout row wrap class="pa-3">
            <v-flex xs12 sm6 md4>
              <div class="filter-display">
                <div class="filter-label">Client</div>
                <div class="filter-value">{{ clientName || 'Loading...' }}</div>
              </div>
            </v-flex>
            <v-flex xs12 sm6 md4>
              <div class="filter-display">
                <div class="filter-label">Date Range</div>
                <div class="filter-value">{{ dateRangeFormatted }}</div>
              </div>
            </v-flex>
            <v-flex xs12 sm6 md4>
              <div class="filter-display">
                <div class="filter-label">State</div>
                <div class="filter-value">{{ stateDisplay }}</div>
              </div>
            </v-flex>
            <v-flex xs12 sm6 md4 v-if="!isGroup && brandDisplay">
              <div class="filter-display">
                <div class="filter-label">Brand</div>
                <div class="filter-value">{{ brandDisplay }}</div>
              </div>
            </v-flex>
            <v-flex xs12 sm6 md4 v-if="distributorDisplay">
              <div class="filter-display">
                <div class="filter-label">Distributor</div>
                <div class="filter-value">{{ distributorDisplay }}</div>
              </div>
            </v-flex>
            <v-flex xs12 sm6 md4 v-if="retailerDisplay">
              <div class="filter-display">
                <div class="filter-label">Retailer</div>
                <div class="filter-value">{{ retailerDisplay }}</div>
              </div>
            </v-flex>
            <v-flex v-for="(label, filter) in groupFilterLabels" :key="filter" xs12 sm6 md4>
              <div class="filter-display">
                <div class="filter-label">{{ filter }}</div>
                <div class="filter-value">{{ label }}</div>
              </div>
            </v-flex>
          </v-layout>
        </material-card>
      </v-flex>

      <v-flex md12 v-if="loading">
        <v-progress-linear indeterminate color="primary"></v-progress-linear>
      </v-flex>

      <v-flex md12 v-if="error">
        <v-alert type="error" :value="true">
          {{ error }}
        </v-alert>
      </v-flex>

      <v-flex md12 v-if="!loading && !error && schedule.length > 0">
        <material-card
          :text="`Real-time shift information - Email ${contactEmail} with updates or questions. Events subject to change.`"
          color="secondary"
          title="Schedule"
        >
          <group-schedule-table
            v-if="isGroup"
            :events="schedule"
            :store-label="storeLabel"
            :state="state" />
          <v-data-table
            v-else
            :headers="computedHeaders"
            :items="schedule"
            disable-initial-sort
            :rows-per-page-items="rowsPerPageItems"
            :pagination.sync="pagination"
          >
            <template slot="headerCell" slot-scope="{ header }">
              <span class="text--darken-3" v-text="header.text" />
            </template>
            <template slot="items" slot-scope="{item}">
              <td>{{ item.subaccount }}</td>
              <td class="wines-column">
                <div v-for="wine in item.wines" :key="wine">• {{ safeTrim(wine) }}</div>
              </td>
              <td class="sub-wines-column">
                <div v-for="sub_wine in item.sub_wines" :key="sub_wine">• {{ safeTrim(sub_wine) }}</div>
              </td>
              <td>{{ item.storename }}</td>
              <td class="text-xs-right">{{ item.warehouse_id }}</td>
              <td>{{ formatDay(item.DOW) }}</td>
              <td>{{ formatDate(item.schedule_date) + "\n" + formatTime(item.time_start) + "-\n" +  formatTime(item.time_end)  }}</td>
              <td>{{ item.city }}</td>
              <td>{{ item.state }}</td>
              <td v-if="hasAgencyColumn">{{ agencyDisplay(item) }}</td>
              <td>{{ lastColumnValue(item) }}</td>
            </template>
          </v-data-table>
        </material-card>
      </v-flex>

      <v-flex md12 v-if="!loading && !error && schedule.length === 0">
        <v-alert type="info" :value="true">
          No events found for the selected criteria. Please check your URL or contact the sender.
        </v-alert>
      </v-flex>

      <v-flex md12 class="text-xs-center mt-4">
        <p class="grey--text">
          This is a shared schedule view. For full access to the dashboard, please contact 
          <a :href="`mailto:${contactEmail}`">{{ contactEmail }}</a>
        </p>
      </v-flex>
    </v-layout>

    <v-btn
      v-if="!loading && !error && schedule.length > 0"
      :loading="csvExporting"
      :disabled="csvExporting"
      @click.native="exportScheduleCSV"
      color="primary"
      fixed
      bottom
      right
      class="download-fab"
    >
      <v-icon left>mdi-cloud-download</v-icon>
      Download
    </v-btn>
  </v-container>
</template>

<script>
import axios from 'axios'
import GroupScheduleTable from '../components/GroupScheduleTable'
import { downloadCsv, scheduleCsv, GROUP_CONTACT_EMAIL } from '../services/group.service'

export default {
  components: { GroupScheduleTable },
  data() {
    return {
      isGroup: false,
      groupFilterLabels: {},
      loading: true,
      error: null,
      schedule: [],
      clientName: '',
      dateStart: '',
      dateEnd: '',
      state: '',
      workOrder: '',
      brand: '',
      distributor: '',
      retailer: '',
      storeLabel: '',
      shareEndpoint: 'clients',
      csvExporting: false,
      rowsPerPageItems: [10, 30, 50, 100],
      pagination: {
        rowsPerPage: 30
      },
      headers: [
        {
          sortable: true,
          text: "Event Type",
          value: 'subaccount'
        },
        {
          sortable: true,
          text: 'Product',
          value: 'wines'
        },
        {
          sortable: true,
          text: 'Substitutions',
          value: 'wine_subs',
          align: 'left'
        },
        {
          sortable: true,
          text: 'Store Name',
          value: 'storename',
          align: 'left'
        },
        {
          sortable: true,
          text: 'Store #',
          value: 'warehouse_id',
          align: 'right'
        },
        {
          sortable: true,
          text: 'Day',
          value: 'DOW',
          align: 'left'
        },
        {
          sortable: true,
          text: 'Shift',
          value: 'schedule_date',
        },
        {
          sortable: true,
          text: 'City',
          value: 'city',
        },
        {
          sortable: true,
          text: 'State',
          value: 'state',
        }
      ]
    }
  },
  computed: {
    contactEmail () {
      return this.isGroup ? GROUP_CONTACT_EMAIL : 'events@hspbrands.com'
    },
    is3pClient() {
      return this.clientName && this.clientName.startsWith('3P')
    },
    computedHeaders() {
      const baseHeaders = [...this.headers]

      if (this.hasAgencyColumn) {
        baseHeaders.push({
          sortable: true,
          text: 'Agency',
          value: 'agency_name'
        })
      }

      const lastHeader = {
        sortable: true,
        text: this.is3pClient ? 'Demo ID' : 'Store Type',
        value: this.is3pClient ? 'demo_id' : 'store_type'
      }

      return [...baseHeaders, lastHeader]
    },
    hasAgencyColumn() {
      return this.schedule.some(item => item && item.agency_name)
    },
    dateRangeFormatted() {
      if (!this.dateStart || !this.dateEnd) return 'No Dates Selected'
      return `${this.formatDate(this.dateStart)} - ${this.formatDate(this.dateEnd)}`
    },
    stateDisplay() {
      return this.state || 'All States'
    },
    brandDisplay() {
      return this.brand && this.brand !== 'All' ? this.brand : null
    },
    distributorDisplay() {
      return this.distributor && this.distributor !== 'All' ? this.distributor : null
    },
    retailerDisplay() {
      return this.retailer && this.retailer !== 'All' ? this.retailer : null
    }
  },
  async created() {
    const token = this.$route.query.token

    if (!token) {
      this.error = 'Invalid link: No token provided. Please check the URL.'
      this.loading = false
      return
    }

    await this.loadSharedSchedule(token)
  },
  methods: {
    applySharedData(data) {
      const tokenData = data.tokenData || {}
      this.groupFilterLabels = data.filterLabels || {}
      const schedule = Array.isArray(data.schedule) ? data.schedule : []
      const filteredSchedule = this.isGroup ? schedule : schedule.filter(obj => obj.outcome === null)

      this.schedule = filteredSchedule
      this.clientName = tokenData.client_name || 'Unknown Client'
      this.dateStart = tokenData.date_start || ''
      this.dateEnd = tokenData.date_end || ''
      this.state = tokenData.state || 'All'
      this.workOrder = tokenData.work_order || 'All'
      this.brand = tokenData.brand || 'All'
      this.distributor = tokenData.distributor || 'All'
      this.retailer = tokenData.retailer || 'All'
      this.storeLabel = tokenData.store_label || ''
    },
    async fetchSharedSchedule(apiRoot, token, endpoint) {
      return axios.get(`${apiRoot}${endpoint}/shared_schedule?token=${token}`)
    },
    lastColumnValue(item) {
      if (this.is3pClient) {
        return item.client_code ? item.client_code + '_' + item.schedule_id : ''
      }

      return item.store_type
    },
    agencyDisplay(item) {
      if (item && item.agency_name) {
        return item.agency_name
      }

      return 'N/A'
    },
    async loadSharedSchedule(token) {
      // Group links are 64 hex; know this before fetching so a failed load still shows the group contact.
      this.isGroup = /^[a-f0-9]{64}$/.test(token)
      try {
        this.loading = true
        this.error = null

        const config = await axios.get('/config/config.json')
        const apiRoot = config.data.api_root

        if (this.isGroup) {
          const response = await this.fetchSharedSchedule(apiRoot, token, 'groups')
          this.shareEndpoint = 'groups'
          this.applySharedData(response.data)
          return
        }

        const clientResponse = await this.fetchSharedSchedule(apiRoot, token, 'clients')
        const clientData = clientResponse.data || {}

        if (!clientData.error && Array.isArray(clientData.schedule)) {
          this.shareEndpoint = 'clients'
          this.applySharedData(clientData)
          return
        }

        const retailResponse = await this.fetchSharedSchedule(apiRoot, token, 'retail')
        const retailData = retailResponse.data || {}

        if (!retailData.error && Array.isArray(retailData.schedule)) {
          this.shareEndpoint = 'retail'
          this.applySharedData(retailData)
          return
        }

        this.error = retailData.error || clientData.error || 'Unable to load schedule. Please try again later or contact events@hspbrands.com'

      } catch (error) {
        console.error('Error loading shared schedule:', error)
        
        if (error.response && [401, 403, 404].includes(error.response.status)) {
          this.error = 'This link is invalid or has expired. Please contact the sender for a new link.'
        } else if (error.response && error.response.status === 410) {
          this.error = 'This link has expired. Please contact the sender for a new link.'
        } else {
          this.error = `Unable to load schedule. Please try again later or contact ${this.contactEmail}`
        }
      } finally {
        this.loading = false
      }
    },

    safeTrim(value) {
      if (typeof value === 'string') return value.trim()
      return String(value == null ? '' : value).trim()
    },

    formatDate(date) {
      if (!date) return null

      const [year, month, day] = date.split('-')
      return `${month}/${day}/${year}`
    },

    formatTime(time) {
      const [hour, minute] = time.split(':')
      let h = parseInt(hour)
      const meridian = h >= 12 ? ' PM' : ' AM'
      if (h > 12) h = h - 12
      if (h === 0) h = 12
      return `${h}:${minute}${meridian}`
    },

    formatDay(dow) {
      const days = {
        "1": "Monday",
        "2": "Tuesday",
        "3": "Wednesday",
        "4": "Thursday",
        "5": "Friday",
        "6": "Saturday",
        "7": "Sunday"
      }
      return days[dow] || ''
    },

    exportScheduleCSV() {
      if (this.isGroup) {
        downloadCsv(`group-schedule-${this.dateStart}.csv`, scheduleCsv(this.schedule, { storeLabel: this.storeLabel, state: this.state }))
        return
      }
      this.csvExporting = true
      const context = this
      const token = this.$route.query.token
      
      if (!token) {
        this.csvExporting = false
        return
      }

      const config = axios.get('/config/config.json')
      config.then((response) => {
        const apiRoot = response.data.api_root
        const endpoint = this.shareEndpoint || 'clients'
        const url = `${apiRoot}${endpoint}/shared_schedule?token=${token}&filetype=csv`
        
        window.location.href = url
        
        setTimeout(function () {
          context.csvExporting = false
        }, 3500)
      })
    }

  }
}
</script>

<style scoped>
.filter-display {
  margin-bottom: 10px;
}

.filter-label {
  font-size: 16px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.filter-value {
  font-size: 18px;
  font-weight: 500;
  color: #333;
}

.wines-column {
  min-width: 320px;
  max-width: 400px;
  white-space: normal;
  word-break: break-word;
}

.sub-wines-column {
  min-width: 230px;
  max-width: 280px;
  white-space: normal;
  word-break: break-word;
}

.v-alert.v-alert {
  border-color: #CAA2DD !important;
}

.my-0 {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.download-fab {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3) !important;
  padding: 0 15px !important;
  min-width: 190px !important;
  height: 56px !important;
  border-radius: 28px !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  margin-right: 30px !important;
}

.download-fab .v-icon {
  color: white !important;
  margin-right: 8px !important;
}
</style>